import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { DataService } from "../../Services/dataService";
import Loader from "../../Components/Loader";

export const RateApprovalList = () => {
  const history = useHistory();
  const [listData, setListData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = () => {
    setLoader(true);
    DataService.get("/admin/get_rate_approval_requests")
      .then((res) => {
        if (res && res.status) {
          setListData(res.data || []);
          setTotal(res.pagination?.total || 0);
        }
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  const handleApprove = (retailer_id) => {
    if (window.confirm("Are you sure you want to approve rate setup for this retailer?")) {
      DataService.post("/admin/approve_retailer_rate", { retailer_id })
        .then((res) => {
          if (res && res.status) {
            alert("Rate setup approved successfully!");
            fetchList();
          } else {
            alert(res?.message || "Failed to approve");
          }
        })
        .catch(() => alert("An error occurred"));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const statusBadge = (status) => {
    if (status === "requested") {
      return (
        <span style={{
          background: "#fff3cd", color: "#856404",
          border: "1px solid #ffc107", padding: "3px 10px",
          borderRadius: "12px", fontSize: "12px", fontWeight: "600",
        }}>
          ⏳ Requested
        </span>
      );
    }
    return (
      <span style={{
        background: "#f8d7da", color: "#842029",
        border: "1px solid #f5c2c7", padding: "3px 10px",
        borderRadius: "12px", fontSize: "12px", fontWeight: "600",
      }}>
        🔴 Pending
      </span>
    );
  };

  const RateDetailsPanel = ({ data }) => {
    if (!data) {
      return (
        <div style={{ padding: "16px", color: "#6c757d", fontStyle: "italic" }}>
          No rate details submitted yet. Retailer has not clicked "Request Rate Setup Approval" from the calculator.
        </div>
      );
    }

    const { shipment, selected_quote, all_quotes_count, requested_at } = data;

    return (
      <div style={{
        padding: "20px 24px",
        background: "linear-gradient(135deg, #f8f9ff 0%, #eef2ff 100%)",
        borderTop: "2px solid #dee2e6",
      }}>
        <h6 style={{ fontWeight: "700", marginBottom: "16px", color: "#333" }}>
          📋 Rate Request Details
          {requested_at && (
            <span style={{ fontSize: "12px", color: "#6c757d", fontWeight: "400", marginLeft: "12px" }}>
              Requested on: {formatDate(requested_at)}
            </span>
          )}
        </h6>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {/* Shipment Info */}
          {shipment && (
            <div style={{
              background: "white", borderRadius: "8px", padding: "16px",
              border: "1px solid #dee2e6", boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
            }}>
              <h6 style={{ fontWeight: "700", color: "#495057", marginBottom: "12px", fontSize: "13px" }}>
                📦 Shipment Details
              </h6>
              <table style={{ width: "100%", fontSize: "13px" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#6c757d", paddingBottom: "6px", width: "45%" }}>Origin:</td>
                    <td style={{ fontWeight: "600", paddingBottom: "6px" }}>
                      {shipment.origin_city || "-"}{shipment.origin_country ? `, ${shipment.origin_country}` : ""}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Destination:</td>
                    <td style={{ fontWeight: "600", paddingBottom: "6px" }}>
                      {shipment.destination_city || "-"}{shipment.destination_country ? `, ${shipment.destination_country}` : ""}
                    </td>
                  </tr>
                  {shipment.goods_type && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Goods Type:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>{shipment.goods_type}</td>
                    </tr>
                  )}
                  {shipment.total_weight && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Total Weight:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>{shipment.total_weight} kg</td>
                    </tr>
                  )}
                  {shipment.total_value && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Goods Value:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>AED {shipment.total_value}</td>
                    </tr>
                  )}
                  {all_quotes_count > 0 && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Quotes Found:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>{all_quotes_count} carriers</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Items */}
              {shipment.items && shipment.items.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <div style={{ fontWeight: "600", fontSize: "12px", color: "#495057", marginBottom: "6px" }}>Items:</div>
                  {shipment.items.map((item, i) => (
                    <div key={i} style={{
                      background: "#f8f9fa", borderRadius: "4px",
                      padding: "6px 10px", marginBottom: "4px", fontSize: "12px"
                    }}>
                      {item.name || "Item " + (i + 1)} — Qty: {item.quantity}, Wt: {item.weight}kg, Val: AED {item.value}
                      {item.hs_code && <span style={{ color: "#6c757d" }}> | HS: {item.hs_code}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Quote */}
          <div style={{
            background: "white", borderRadius: "8px", padding: "16px",
            border: selected_quote ? "1px solid #28a745" : "1px solid #dee2e6",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
          }}>
            <h6 style={{ fontWeight: "700", color: selected_quote ? "#28a745" : "#495057", marginBottom: "12px", fontSize: "13px" }}>
              {selected_quote ? "✅ Selected Quote" : "ℹ️ No Quote Selected"}
            </h6>
            {selected_quote ? (
              <table style={{ width: "100%", fontSize: "13px" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#6c757d", paddingBottom: "6px", width: "45%" }}>Courier:</td>
                    <td style={{ fontWeight: "700", paddingBottom: "6px" }}>{selected_quote.courier || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Service:</td>
                    <td style={{ fontWeight: "600", paddingBottom: "6px" }}>{selected_quote.service || "-"}</td>
                  </tr>
                  {selected_quote.freight_charge && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Freight:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>
                        {selected_quote.currency} {selected_quote.freight_charge}
                      </td>
                    </tr>
                  )}
                  {selected_quote.total_amount && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Total Amount:</td>
                      <td style={{ fontWeight: "700", color: "#28a745", fontSize: "15px", paddingBottom: "6px" }}>
                        {selected_quote.currency} {selected_quote.total_amount}
                      </td>
                    </tr>
                  )}
                  {selected_quote.transit_days && (
                    <tr>
                      <td style={{ color: "#6c757d", paddingBottom: "6px" }}>Transit Days:</td>
                      <td style={{ fontWeight: "600", paddingBottom: "6px" }}>{selected_quote.transit_days} days</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
                The retailer requested approval without selecting a specific quote. They may have been browsing rates only.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <div>
                <h4 className="card-title" style={{ marginBottom: "4px" }}>Rate Approval Requests</h4>
                <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
                  Review shipment details and approve retailers to start booking
                </p>
              </div>
              <div style={{
                background: "#fff3cd", border: "1px solid #ffc107",
                borderRadius: "8px", padding: "8px 16px",
                fontSize: "13px", fontWeight: "600", color: "#856404",
              }}>
                {total} Pending Request{total !== 1 ? "s" : ""}
              </div>
            </div>

            {loader ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader />
              </div>
            ) : listData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
                <h5>No Pending Approval Requests</h5>
                <p>All retailers have been reviewed.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover" style={{ borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th>#</th>
                      <th>Retailer</th>
                      <th>Phone</th>
                      <th>Account Status</th>
                      <th>Rate Request Status</th>
                      <th>Registered</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listData.map((item, idx) => (
                      <React.Fragment key={item._id}>
                        <tr style={{ cursor: "pointer" }}>
                          <td
                            onClick={() => setExpandedRow(expandedRow === item._id ? null : item._id)}
                            style={{ textAlign: "center", color: "#0323fe", fontWeight: "bold" }}
                          >
                            {expandedRow === item._id ? "▲" : "▼"}
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <strong>{item.retailer_name || "(No name)"}</strong>
                            <br />
                            <small style={{ color: "#6c757d" }}>{item.retailer_email}</small>
                          </td>
                          <td>{item.phone_number || "-"}</td>
                          <td>
                            <span className={"badge rounded-pill font-size-12 " + (item.retailer_status === "ACTIVE" ? "bg-success" : "bg-soft-danger")}>
                              {item.retailer_status || "INACTIVE"}
                            </span>
                          </td>
                          <td>{statusBadge(item.rate_approval_status)}</td>
                          <td>{formatDate(item.createdAt)}</td>
                          <td>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {item.rate_approval_status === "requested" && (
                                <button
                                  onClick={() => handleApprove(item._id)}
                                  style={{
                                    background: "linear-gradient(135deg, #28a745, #20c997)",
                                    color: "white", border: "none", borderRadius: "6px",
                                    padding: "6px 14px", fontSize: "12px",
                                    cursor: "pointer", fontWeight: "600",
                                    boxShadow: "0 2px 6px rgba(40,167,69,0.3)",
                                  }}
                                >
                                  ✓ Approve
                                </button>
                              )}
                              <button
                                onClick={() => history.push("/retailer-details?id=" + item._id)}
                                style={{
                                  background: "transparent", color: "#0323fe",
                                  border: "1px solid #0323fe", borderRadius: "6px",
                                  padding: "6px 12px", fontSize: "12px",
                                  cursor: "pointer", fontWeight: "600",
                                }}
                              >
                                Profile
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expandable Rate Details Row */}
                        {expandedRow === item._id && (
                          <tr>
                            <td colSpan="8" style={{ padding: 0 }}>
                              <RateDetailsPanel data={item.rate_request_data} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
